import { useState } from 'react';
import { MapPin, Phone, Clock, Mail, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm({ ...form, phone: val });
    if (val.length > 0 && val.length < 10) setPhoneError('Enter a valid 10-digit mobile number');
    else if (val.length === 10 && !/^[6-9]\d{9}$/.test(val)) setPhoneError('Must start with 6, 7, 8, or 9');
    else setPhoneError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Please fill required fields'); return; }
    if (form.phone && !/^[6-9]\d{9}$/.test(form.phone)) { toast.error('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try { await axios.post(`${API}/contact`, { ...form, phone: form.phone ? `+91${form.phone}` : '' }); toast.success('Message sent! We\'ll get back to you soon.'); setForm({ name: '', email: '', phone: '', message: '' }); setPhoneError(''); } catch { toast.error('Failed to send'); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="text-center mb-12">
          <span className="font-body text-xs uppercase tracking-[0.2em] text-rust font-medium">Get In Touch</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-dark-brown mt-2">Contact Us</h1>
          <p className="font-body text-base text-light-brown mt-3 max-w-md mx-auto">Have questions? We'd love to hear from you.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="bg-white rounded-2xl border border-border-custom p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div><Label className="font-body text-sm text-dark-brown">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-cream/50 border-border-custom" data-testid="contact-name" required /></div>
              <div><Label className="font-body text-sm text-dark-brown">Email *</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" className="mt-1 bg-cream/50 border-border-custom" data-testid="contact-email" required /></div>
              <div><Label className="font-body text-sm text-dark-brown">Phone <span className="text-light-brown font-normal">(optional)</span></Label>
                <div className="flex mt-1">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-cream text-mid-brown font-body text-sm">+91</span>
                  <Input value={form.phone} onChange={handlePhoneChange} placeholder="10-digit mobile number" inputMode="numeric" maxLength={10}
                    className={`rounded-l-none bg-cream/50 border-border-custom ${phoneError ? 'border-red-400' : (form.phone.length === 10 && !phoneError) ? 'border-green-400' : ''}`} data-testid="contact-phone" />
                </div>
                {phoneError && <p className="font-body text-xs text-red-500 mt-1">{phoneError}</p>}
                {form.phone.length === 10 && !phoneError && <p className="font-body text-xs text-green-600 mt-1">✓ Valid number</p>}
              </div>
              <div><Label className="font-body text-sm text-dark-brown">Message *</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="mt-1 bg-cream/50 border-border-custom resize-none" data-testid="contact-message" required /></div>
              <Button type="submit" disabled={loading} className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-12" data-testid="contact-submit-btn">
                {loading ? <span className="spinner mr-2"></span> : <Send className="w-4 h-4 mr-2" />} Send Message
              </Button>
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-border-custom p-6">
              <h3 className="font-heading text-xl font-bold text-dark-brown mb-5">Visit Our Shop</h3>
              {[
                { icon: MapPin, label: 'Address', value: 'Mattan Chowk, Anantnag\nJammu & Kashmir, India - 192101' },
                { icon: Phone, label: 'Phone / WhatsApp', value: '+91 9541399210' },
                { icon: Mail, label: 'Email', value: 'info@petsvalley.com' },
                { icon: Clock, label: 'Hours', value: 'Mon-Sat: 9AM-8PM\nSunday: 10AM-6PM' },
              ].map(info => (
                <div key={info.label} className="flex gap-4 mb-5 last:mb-0">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-pale flex items-center justify-center"><info.icon className="w-5 h-5 text-rust" /></div>
                  <div><div className="font-body text-sm font-medium text-dark-brown">{info.label}</div><div className="font-body text-sm text-light-brown whitespace-pre-line">{info.value}</div></div>
                </div>
              ))}
            </div>


            <div className="bg-pale rounded-2xl p-6 text-center">
              <h4 className="font-heading text-lg font-bold text-dark-brown mb-2">Wholesale Inquiries</h4>
              <p className="font-body text-sm text-light-brown">Call or visit in person for bulk pricing.</p>
              <a href="tel:+919541399210" className="inline-block mt-3">
                <Button className="bg-dark-brown hover:bg-dark-brown/90 text-cream font-body rounded-full px-6"><Phone className="w-4 h-4 mr-2" /> Call Now</Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
